import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CardLoading from "./CardLoading";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import { TC } from "../utils/trustCallApi";
import { emailSettingsSchema } from "../utils/validationSchemas";
import { useCanWrite, AGENT_READ_ONLY_MESSAGE } from "../hooks/useAccountConfig";
import AgentReadOnlyBanner from "./AgentReadOnlyBanner";

type EmailFormData = z.infer<typeof emailSettingsSchema>;

// MVC email regex from Monitor.cshtml line 5917
const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const NotificationSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);

  const settingsQuery = useQuery({
    queryKey: ["trustcall", "settings", "email-frequency"],
    queryFn: () => callApi(TC.SETTINGS_EMAIL, HttpMethod.GET),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSettingsSchema),
    defaultValues: {
      emailAddress: "",
      emailFrequency: "2",
      scamScoreEmailFrequency: 2,
    },
  });

  // Populate form when data loads
  useEffect(() => {
    if (settingsQuery.data) {
      reset({
        emailAddress: settingsQuery.data.emailAddress ?? "",
        emailFrequency: settingsQuery.data.emailFrequency ?? "2",
        scamScoreEmailFrequency: settingsQuery.data.scamScoreEmailFrequency ?? 2,
      });
    }
  }, [settingsQuery.data, reset]);

  const saveMutation = useMutation({
    mutationFn: (data: EmailFormData) =>
      callApi(TC.SETTINGS_EMAIL, HttpMethod.PUT, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trustcall", "settings"] });
      toast.success("Email notification settings saved");
    },
    onError: () => toast.error("Failed to save email settings"),
  });

  // MVC line 5912-5930: validate each email line-by-line, save valid ones, show modal for invalid
  const onSubmit = (data: EmailFormData) => {
    const lines = data.emailAddress.trim().split(/[\n,]+/).map((l) => l.trim()).filter(Boolean);
    const valid: string[] = [];
    const invalid: string[] = [];

    lines.forEach((email) => {
      if (EMAIL_PATTERN.test(email.toLowerCase())) {
        valid.push(email);
      } else {
        invalid.push(email);
      }
    });

    if (invalid.length > 0) {
      setInvalidEmails(invalid);
    }

    // MVC saves valid emails even when some are invalid
    if (valid.length > 0) {
      saveMutation.mutate({
        ...data,
        emailAddress: valid.join(","),
      });
    }
  };

  if (settingsQuery.isLoading) {
    return <CardLoading />;
  }

  return (
    <>
      <AgentReadOnlyBanner />
      {/* DNC-390: readOnly keeps the addresses selectable; a disabled fieldset would not.
          Radios take disabled — readOnly is inert on them. */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          {/* Left card — Email addresses */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header tc-card-header">
                <h6 className="mb-0" style={{ color: "white" }}>Email reports to these addresses</h6>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-2">
                  To add multiple email addresses, place each address on a separate line
                </p>
                <textarea
                  className={`form-control ${errors.emailAddress ? "is-invalid" : ""}`}
                  rows={10}
                  placeholder="alerts@example.com"
                  readOnly={!canWrite}
                  {...register("emailAddress")}
                />
                {errors.emailAddress && (
                  <div className="invalid-feedback">{errors.emailAddress.message}</div>
                )}
              </div>
            </div>
          </div>

          {/* Right card — Email frequency */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header tc-card-header">
                <h6 className="mb-0" style={{ color: "white" }}>Email frequency reports</h6>
              </div>
              <div className="card-body">
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="freq-daily"
                    value="2"
                    disabled={!canWrite}
                    {...register("emailFrequency")}
                  />
                  <label className="form-check-label" htmlFor="freq-daily">
                    Receive <strong>Daily</strong> reports with each phone numbers Current Spam Score, Historical Spam Score, and Maximum Spam Score.
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="freq-weekly"
                    value="3"
                    disabled={!canWrite}
                    {...register("emailFrequency")}
                  />
                  <label className="form-check-label" htmlFor="freq-weekly">
                    Receive <strong>Weekly</strong> reports with each phone numbers Current Spam Score, Historical Spam Score, and Maximum Spam Score.
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full-width Save button */}
        <button
          type="submit"
          className="btn w-100 mt-3"
          style={{ backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff" }}
          disabled={saveMutation.isPending || !canWrite}
          title={!canWrite ? AGENT_READ_ONLY_MESSAGE : undefined}
        >
          <svg viewBox="0 0 512 512" width="14" height="14" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
            <path d="M384 32H64c-17.6 0-32 14.4-32 32v384c0 17.6 14.4 32 32 32h384c17.6 0 32-14.4 32-32V128zm-64 32v128h-64V64zm128 384-383.9.1-.1-.1V64.1l.1-.1H96v160h256V64h18.7l77.3 77.3z" />
          </svg>
          <span style={{ verticalAlign: "middle" }}>{saveMutation.isPending ? "Saving..." : "Save"}</span>
        </button>
      </form>

      {/* Invalid Email Modal — matches MVC #emailModal */}
      {invalidEmails.length > 0 && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: "#ff0000" }}>
                <h5 className="modal-title" style={{ color: "#fff" }}>Invalid email address</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setInvalidEmails([])} />
              </div>
              <div className="modal-body">
                <table className="table table-bordered">
                  <tbody>
                    {invalidEmails.map((email, i) => (
                      <tr key={i}>
                        <td>Email Address &quot;{email}&quot; is invalid</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationSettings;
